import { useMemo } from 'react';
import { WORLD_COUNTRY_LIST } from '../../../../assets/countryList';
import { capitalize, inRange, max, min, uniq, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { SEARCH_PARAMS_KEYS } from '../../../router/searchParamsKeys';
import { Plot } from '../../../shared/types';
import { Filter } from '../../../shared/components/NewTable/Filters/Filters';
import {
  min as getMinDate,
  max as getMaxDate,
  isWithinInterval,
  isSameDay,
  parse,
  endOfDay,
} from 'date-fns';
import { FILTER_DATE_FORMAT_PARSER } from '../../../shared/components/NewTable/Filters/DateFilter';

const usePlotTableFilters = (plots: Plot[] = []): Filter<Plot>[] => {
  const { t } = useTranslation();

  const filterOptions = useMemo(
    () => ({
      commodities: uniq(plots?.map((plot) => plot.commodities).flat())?.map(
        (commodity) => ({
          label: capitalize(commodity),
          value: commodity,
        })
      ),
      countries: uniq(plots?.map((plot) => plot.countryCode)).map(
        (country) => ({
          label:
            WORLD_COUNTRY_LIST?.find((c) => c.value === country)?.label ??
            country ??
            '',
          value: country ?? '',
        })
      ),
      suppliers: uniqBy(plots?.map((plot) => plot.suppliers).flat(), 'id')?.map(
        (supplier) => ({
          label: supplier.name,
          value: supplier.id,
        })
      ),
      surfaceArea: {
        min: min(plots?.map((plot) => plot.surfaceAreaHectares)),
        // Add 1 to the max value to include floating numbers in the range
        max: (max(plots?.map((plot) => plot.surfaceAreaHectares)) ?? 0) + 1,
      },
      plotAssessmentStatus: uniq(
        plots?.map((plot) => plot?.riskAssessment?.status ?? 'NEW')
      )?.map((status) => ({
        label: t(`suppliers.riskAssessment.status.${status}`),
        value: status,
      })),
      updatedAt: {
        min: getMinDate(plots?.map((plot) => new Date(plot.updatedAt))),
        max: getMaxDate(plots?.map((plot) => new Date(plot.updatedAt))),
      },
    }),
    [plots]
  );

  const filters = [
    {
      label: t('map.sidePanel.details.properties.commodities'),
      key: SEARCH_PARAMS_KEYS.FILTERS_COMMODITIES,
      match: (record: Plot, value: string) =>
        value
          ?.split(',')
          ?.some((commodity) => record?.commodities?.includes(commodity)),
      type: 'select' as const,
      options: filterOptions.commodities,
    },
    {
      label: t('map.sidePanel.details.properties.country'),
      key: SEARCH_PARAMS_KEYS.FILTERS_COUNTRY,
      match: (record: Plot, value: string) =>
        value?.split(',')?.some((country) => record.countryCode === country),
      type: 'select' as const,
      options: filterOptions.countries,
    },
    {
      label: t('common.roles.supplier'),
      key: SEARCH_PARAMS_KEYS.FILTERS_SUPPLIER,
      match: (record: Plot, value: string) =>
        value
          ?.split(',')
          ?.some((supplier) =>
            record.suppliers.some(({ id }) => id === supplier)
          ),
      type: 'select' as const,
      options: filterOptions.suppliers,
    },
    {
      label: t('plots.table.status'),
      key: SEARCH_PARAMS_KEYS.FILTERS_STATUS,
      match: (record: Plot, value: string) => {
        const status = record?.riskAssessment?.status ?? 'NEW';
        return value?.split(',')?.some((urlStatus) => status === urlStatus);
      },
      type: 'select' as const,
      options: filterOptions.plotAssessmentStatus,
    },
    {
      label: t('plots.table.updatedAt'),
      key: SEARCH_PARAMS_KEYS.FILTERS_UPDATED_AT,
      type: 'date' as const,
      range: filterOptions.updatedAt,
      match: (record: Plot, value: string) => {
        const [start, end] = value?.split(',') ?? [];
        const startDate = start
          ? parse(start, FILTER_DATE_FORMAT_PARSER, new Date())
          : undefined;
        const endDate = end
          ? endOfDay(parse(end, FILTER_DATE_FORMAT_PARSER, new Date()))
          : undefined;

        if (startDate && !endDate) {
          return isSameDay(new Date(record.updatedAt), startDate);
        }

        if (startDate && endDate) {
          return isWithinInterval(new Date(record.updatedAt), {
            start: startDate,
            end: endDate,
          });
        }

        return true;
      },
    },
    {
      label: t('plots.table.surfaceArea'),
      key: SEARCH_PARAMS_KEYS.FILTERS_SURFACE_AREA,
      type: 'slider' as const,
      range: filterOptions.surfaceArea,
      suffix: t('plots.filters.areaUnit'),
      minLabel: t('plots.filters.minArea'),
      maxLabel: t('plots.filters.maxArea'),
      match: ({ surfaceAreaHectares }: Plot, value: string) => {
        const [min, max] = value?.split(',') ?? [];

        if (inRange(Number(surfaceAreaHectares), Number(min), Number(max))) {
          return true;
        }

        return false;
      },
    },
    {
      label: t('plots.table.risk'),
      key: SEARCH_PARAMS_KEYS.FILTERS_RISK,
      match: (record: Plot, value: string) => record.riskStatus === value,
      type: 'radio' as const,
      options: [
        { label: t('common.status.high'), value: 'high' },
        { label: t('common.status.low'), value: 'low' },
        { label: t('common.status.processing'), value: 'processing' },
      ],
    },
    {
      label: t('plots.filters.precision'),
      key: SEARCH_PARAMS_KEYS.FILTERS_PRECISION,
      match: (record: Plot) =>
        typeof record.mostRecentAnalysesByType?.PRECISION
          ?.riskOfDeforestation === 'number',
      options: [{ label: 'Precision', value: 'true' }],
      type: 'switch' as const,
    },
  ];

  return filters;
};

export default usePlotTableFilters;
